import moment from "moment";
import { useEffect } from "react";

import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AttributionIcon from "@mui/icons-material/Attribution";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { visuallyHidden } from "@mui/utils";
import { Box, Chip, Container, Paper, TableContainer, TablePagination, TableSortLabel, Tooltip } from "@mui/material";

import conf from "../../conf";
import Manage from "./Manage";
import Invite from "./Invite";
import format from "../utils/Role";
import Refresh from "../utils/Refresh";
import LinearLoader from "../utils/LinearLoader";
import SectionHeader from "../utils/SectionHeader";
import usePagination from "../../hooks/pagination";
import ProtectedRoute from "../auth/ProtectedRoute";
import { User, useAuth } from "../auth/AuthProvider";
import { useConfgUsers } from "../../hooks/confg/base";
import { Offline, Online } from "../utils/Status";

type UserRowProps = { user: User; isAdmin: boolean };

const UserRow = (props: UserRowProps) => {
  const { user, isAdmin } = props;
  const { email, activeOrganization, firstName, lastName, lastActive } = user;

  const { user: self } = useAuth();

  const { role, status } = user.organizations.find((org) => org.id === self.activeOrganization.id) || {};
  const disabled = self.id === user.id;
  const isSysAdmin = self.activeOrganization.role === "sys.admin";

  const Status = status ? (
    <Chip
      size="small"
      label={status}
      color={status === "active" ? "success" : status === "pending" || status === "invited" ? "warning" : "error"}
    />
  ) : (
    "--"
  );

  const EmailIcon =
    email === self.email ? (
      <AttributionIcon fontSize="small" color="success" />
    ) : user.session.status === "active" ? (
      Online
    ) : (
      Offline
    );

  return (
    <TableRow sx={conf.STYLE.LAST_CHILD_NO_BORDER}>
      <TableCell component="th" scope="row">
        <Chip size="small" label={email} icon={EmailIcon} />
      </TableCell>

      <TableCell align="right">{Status}</TableCell>

      {!isSysAdmin && <TableCell align="right">{format(role || "--")}</TableCell>}

      {isSysAdmin && <TableCell align="right">{format(activeOrganization.name || "--")}</TableCell>}

      {isSysAdmin && <TableCell align="right">{format(activeOrganization.role || "--")}</TableCell>}

      <TableCell align="right">{firstName?.toLowerCase() || "--"}</TableCell>

      <TableCell align="right">{lastName?.toLowerCase() || "--"}</TableCell>

      <TableCell align="right">{moment(lastActive).fromNow()}</TableCell>

      {isAdmin && (
        <TableCell align="right">
          <Manage user={user} disabled={disabled} />
        </TableCell>
      )}
    </TableRow>
  );
};

const Users = () => {
  const { user: self } = useAuth();
  const isAdmin = self.activeOrganization.role === "org.admin";
  const COLUMNS = self.activeOrganization.role === "sys.admin" ? conf.COLUMNS.USERS.SYS : conf.COLUMNS.USERS.BASE;

  const paginate = usePagination({ defaultSort: "email", defaultOrder: "asc" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const users = useConfgUsers(body);

  useEffect(() => {
    document.title = "[semiauth] | users";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container maxWidth="xl" sx={{ marginBottom: 4 }}>
        <SectionHeader
          title="Users"
          icon={<PeopleAltIcon />}
          buttons={[
            ...(isAdmin ? [<Invite key={"users-invite"} />] : []),
            <TablePagination
              key={"users-pagination"}
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={users?.data?.meta.total || 0}
              page={page}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
            />,
            <Refresh key={"users-refresh"} onClick={users.refetch} isFetching={users.isFetching} />
          ]}
        />

        <Paper elevation={3}>
          <TableContainer sx={{ maxHeight: 630 }}>
            <Table aria-label="collapsible table" stickyHeader>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableCell key={column.key} sortDirection={order} align={column.isHeader ? "left" : "right"}>
                      <TableSortLabel
                        active={sort === column.key}
                        direction={sort === column.key ? order : "asc"}
                        onClick={(event: React.MouseEvent<unknown>) => handleSort(event, column.key)}
                      >
                        {column.description ? (
                          <Tooltip title={column.description} placement="top">
                            <b>{column.label}</b>
                          </Tooltip>
                        ) : (
                          <b>{column.label}</b>
                        )}

                        {sort === column.key ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc" ? "sorted descending" : "sorted ascending"}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  ))}

                  {isAdmin && (
                    <TableCell align="right">
                      <AssignmentIndIcon fontSize="inherit" />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              {users.isLoading ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={8}>
                      <LinearLoader />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {users?.data?.data?.map((user: User) => (
                    <UserRow key={user.email} user={user} isAdmin={isAdmin} />
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </ProtectedRoute>
  );
};

export default Users;
